/// <amd-module name="Wit/Common/WiqlValues" />
// Copied from Tfs\Service\WebAccess\WorkItemTracking\Scripts\OM\WiqlValues.ts
import { format } from "VSS/Core/Util/String";
import * as WITCommonResources from "Wit/Common/Resources";
import { getInvariantOperator, getLocalizedOperator, WiqlOperators } from "./WiqlOperators";

export interface IParsedCurrentIteration {
    team?: string;
    offset: number;
}

const currentIterationMatchingRegexStr = `\\s*(?:\\(\\s*'(.*?)'\\s*\\))?\\s*(?:(\\+|-)\\s*((?:\\+|-)?\\d*)?)?`;

// Example match: "@currentIteration('team') + 1"
// Capture Group:                               1                               2              3
const currentIterationRegex = new RegExp(`^@${WITCommonResources.WiqlOperators_MacroCurrentIteration}${currentIterationMatchingRegexStr}$`, "i");
const currentIterationRegex_Invariant = new RegExp(`^${WiqlOperators.MacroCurrentIteration}${currentIterationMatchingRegexStr}$`, "i");

/**
 * Parse '@currentiteration(team) + offset'
 * @param text
 * @param useInvariantMacro A boolean flag, if true, it matches the text with invariant macro regex
 */
export function parseCurrentIteration(text: string, useInvariantMacro: boolean = false): IParsedCurrentIteration | null {
    const match = text && text.trim().match(useInvariantMacro ? currentIterationRegex_Invariant : currentIterationRegex);
    if (!match) {
        return null;
    }
    const [, team, plusMinus, offsetText] = match;
    let offset = offsetText ? (plusMinus === "+" ? +offsetText : -offsetText) : 0;
    return { team, offset };
}

export interface IParsedAreas {
    team?: string;
}

// Example match: "@Areas('Team')"
// Capture Group:                                                         1
const areasRegex = new RegExp(`^@${WITCommonResources.WiqlOperators_MacroTeamAreas}\\s*(?:\\(\\s*'([^']*?)'\\s*\\))?$`, "i");

/**
 * Parse '@TeamAreas(team)'
 * @param text
 */
export function parseTeamAreas(text: string): IParsedAreas | null {
    const match = text && text.trim().match(areasRegex);
    if (!match) {
        return null;
    }
    const [, team] = match;

    return { team };
}

export interface IParsedDateMacro {
    invariantMacro?: string;
    modifier?: string;
    offset?: string;
}

// Example match: '@StartOfYear('-1d')+3',  '@StartOfMonth('-1d')',  '@StartOfWeek+3',  '@StartOfDay', '@StartOfMonth('-1')'
// Capture Group:                             1                                          2
const dateMacroTemplate = "^@{0}\\s*(?:\\(\\s*'([+-]{{0,1}}\\d{{1,7}}[yMwdhm]?)'\\s*\\))?\\s*((?:[+-]\\s*\\d{{1,7}}))?$";

const macroRegExMap: { [macro: string]: RegExp } = {
    [WiqlOperators.MacroStartOfYear]: new RegExp(format(dateMacroTemplate, WITCommonResources.WiqlOperators_MacroStartOfYear), "i"),
    [WiqlOperators.MacroStartOfMonth]: new RegExp(format(dateMacroTemplate, WITCommonResources.WiqlOperators_MacroStartOfMonth), "i"),
    [WiqlOperators.MacroStartOfWeek]: new RegExp(format(dateMacroTemplate, WITCommonResources.WiqlOperators_MacroStartOfWeek), "i"),
    [WiqlOperators.MacroStartOfDay]: new RegExp(format(dateMacroTemplate, WITCommonResources.WiqlOperators_MacroStartOfDay), "i")
};

/**
 * Parse '@StartOfYear('-1d')+3',  '@StartOfMonth('-1d')+3',  '@StartOfWeek('-1d')+3',  '@StartOfDay('-1d')+3'
 * @param text
 */
export function parseDateMacro(text: string): IParsedDateMacro | null {
    for (const macro in macroRegExMap) {
        const match = text && text.trim().match(macroRegExMap[macro]);
        if (match) {
            const [, modifier, offset] = match;
            return { invariantMacro: macro, modifier, offset };
        }
    }

    return null;
}

export interface IParsedTodayMacro {
    modifier?: string;
    offset?: string;
}

const localizedTodayMacroRegExp = new RegExp(`^${getLocalizedOperator(WiqlOperators.MacroToday)}\\s*([+-])*\\s*([0-9])*$`);
const invariantTodayMacroRegExp = new RegExp(`^${getInvariantOperator(WiqlOperators.MacroToday)}\\s*([+-])*\\s*([0-9])*$`);

/**
 * Parse '@Today + 3', '@Today - 3', etc
 * @param text
 */
export function parseTodayMacro(text: string, useInvariantMacro = false): IParsedTodayMacro | undefined {
    const regExp = useInvariantMacro ? invariantTodayMacroRegExp : localizedTodayMacroRegExp;
    const match = text && text.trim().match(regExp);
    if (match) {
        const [, modifier, offset] = match;
        return { modifier, offset };
    }
}
